import { Box, VStack, Input, Text, Image, Alert, AlertIcon, Spinner, useToast } from '@chakra-ui/react';
import { useState, useCallback, useEffect } from 'react';
import { InsightLogoPlaceholder } from '../../Header';

interface FeatureEnhanceProps {
  API_URL: string;
}

export function FeatureEnhance({ API_URL }: FeatureEnhanceProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [enhancedUrl, setEnhancedUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedFile(file);
      const preview = URL.createObjectURL(file);
      setPreviewUrl(preview);
      handleEnhance(file);
    }
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    setError(null);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      setSelectedFile(file);
      const preview = URL.createObjectURL(file);
      setPreviewUrl(preview);
      handleEnhance(file);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleEnhance = async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    
    setIsLoading(true);
    setError(null);
    setEnhancedUrl(null);
    
    try {
      const response = await fetch(`${API_URL}/api/enhance`, {
        method: 'POST',
        body: formData,
      });
      
      if (!response.ok) {
        throw new Error('Failed to enhance image');
      }
      
      const blob = await response.blob();
      const enhancedImageUrl = URL.createObjectURL(blob);
      setEnhancedUrl(enhancedImageUrl);
      
    } catch (error: any) {
      setError(error.message || 'An error occurred during enhancement');
      toast({
        title: 'Error',
        description: error.message || 'An error occurred during enhancement',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      if (enhancedUrl) URL.revokeObjectURL(enhancedUrl);
    };
  }, [previewUrl, enhancedUrl]);

  return (
    <VStack spacing={8} w="full" maxW="800px" mx="auto">
      {error && (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          {error}
        </Alert>
      )}

      <Box
        w="full"
        h="400px"
        border="3px dashed"
        borderColor={isDragging ? "blue.400" : "gray.500"}
        borderRadius="xl"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        bg={isDragging ? 'rgba(66, 153, 225, 0.1)' : 'transparent'}
        transition="all 0.2s"
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        position="relative"
        _hover={{ borderColor: "blue.400" }}
        p={8}
      >
        <Input
          type="file"
          height="100%"
          width="100%"
          position="absolute"
          top="0"
          left="0"
          opacity="0"
          aria-hidden="true"
          accept="image/*"
          onChange={handleFileChange}
        />
        
        {previewUrl ? (
          <Image
            src={previewUrl}
            alt="Preview"
            maxH="220px"
            objectFit="contain"
            borderRadius="md"
          />
        ) : (
          <InsightLogoPlaceholder isDragging={isDragging} />
        )}
        
        <Text fontSize="xl" mb={2} mt={4}>
          {selectedFile ? selectedFile.name : 'Drag and drop an image here'}
        </Text>
        <Text fontSize="md" color="gray.500">
          or click to select a file
        </Text>
      </Box>

      {isLoading && (
        <Box textAlign="center">
          <Spinner size="xl" />
          <Text mt={2}>Enhancing image...</Text>
        </Box>
      )}

      {enhancedUrl && (
        <Box w="full">
          <Text fontSize="xl" mb={6} textAlign="center" fontWeight="bold">
            Results
          </Text>
          <Box 
            display="flex" 
            gap={8} 
            justifyContent="center"
            alignItems="flex-start"
          >
            <Box flex="1" maxW="50%">
              <Text fontSize="lg" mb={3} textAlign="center" color="gray.300">
                Original Image
              </Text>
              <Image
                src={previewUrl || undefined}
                alt="Original"
                w="full"
                h="400px"
                objectFit="contain"
                borderRadius="md"
              />
            </Box>
            <Box flex="1" maxW="50%">
              <Text fontSize="lg" mb={3} textAlign="center" color="gray.300">
                Enhanced Image
              </Text>
              <Image
                src={enhancedUrl || undefined}
                alt="Enhanced"
                w="full"
                h="400px"
                objectFit="contain"
                borderRadius="md"
              />
            </Box>
          </Box>
        </Box>
      )}
    </VStack>
  );
}